/**
 * Cached Canada-biased geocoding shared by footprint pins, mismatch checks,
 * and Site Inspector search. Session-scoped only (see googleMapsSessionCache).
 */

import { importLibrary } from '@googlemaps/js-api-loader';
import { configureGoogleMapsLoader } from './googleMapsRuntime';
import {
  geocodeCacheKey,
  geocodeSessionCache,
  normalizeGeocodeAddressKey,
} from './googleMapsSessionCache';
import type { LatLngPoint } from './employerSiteViewpoints';

export type GeocodeLocationType =
  | 'ROOFTOP'
  | 'RANGE_INTERPOLATED'
  | 'GEOMETRIC_CENTER'
  | 'APPROXIMATE'
  | string;

export type GeocodeCandidate = {
  formattedAddress: string;
  location: LatLngPoint;
  locationType: GeocodeLocationType;
  partialMatch: boolean;
  placeId: string | null;
};

export type GeocodeSuccess = {
  ok: true;
  candidates: GeocodeCandidate[];
  status: string;
};

export type GeocodeFailure = {
  ok: false;
  status: 'ZERO_RESULTS' | 'ERROR';
  error: string;
  hardFailure: boolean;
};

export type GeocodeResult = GeocodeSuccess | GeocodeFailure;

const PRECISE_TYPES = new Set(['ROOFTOP', 'RANGE_INTERPOLATED']);

function locationTypeRank(t: string): number {
  if (t === 'ROOFTOP') return 0;
  if (t === 'RANGE_INTERPOLATED') return 1;
  if (t === 'GEOMETRIC_CENTER') return 2;
  return 3;
}

export function sortGeocodeCandidates(
  candidates: GeocodeCandidate[],
  bias: LatLngPoint | null | undefined,
): GeocodeCandidate[] {
  return [...candidates].sort((a, b) => {
    const rank = locationTypeRank(a.locationType) - locationTypeRank(b.locationType);
    if (rank !== 0) return rank;
    if (a.partialMatch !== b.partialMatch) return a.partialMatch ? 1 : -1;
    if (bias) {
      const da =
        (a.location.lat - bias.lat) ** 2 + (a.location.lng - bias.lng) ** 2;
      const db =
        (b.location.lat - bias.lat) ** 2 + (b.location.lng - bias.lng) ** 2;
      return da - db;
    }
    return 0;
  });
}

export function isAmbiguousGeocode(candidates: GeocodeCandidate[]): boolean {
  if (candidates.length <= 1) {
    return Boolean(candidates[0]?.partialMatch);
  }
  const top = candidates[0];
  const second = candidates[1];
  if (!top || !second) return candidates.length > 1;
  if (top.partialMatch) return true;
  if (PRECISE_TYPES.has(top.locationType) && PRECISE_TYPES.has(second.locationType)) {
    return true;
  }
  return candidates.length > 1 && !PRECISE_TYPES.has(top.locationType);
}

function mapGeocodeError(err: unknown): GeocodeFailure {
  const message = err instanceof Error ? err.message : String(err ?? 'Geocode failed');
  const upper = message.toUpperCase();
  const hardFailure =
    upper.includes('OVER_QUERY_LIMIT') ||
    upper.includes('REQUEST_DENIED') ||
    upper.includes('INVALID_REQUEST') ||
    upper.includes('UNKNOWN_ERROR') ||
    upper.includes('NETWORK') ||
    upper.includes('FAILED TO FETCH') ||
    upper.includes('BILLING');

  if (upper.includes('ZERO_RESULTS')) {
    return {
      ok: false,
      status: 'ZERO_RESULTS',
      error: 'No matching address found.',
      hardFailure: false,
    };
  }

  return {
    ok: false,
    status: 'ERROR',
    error: hardFailure
      ? `Address lookup failed (${message}). Check Maps billing and API key restrictions.`
      : message,
    hardFailure,
  };
}

/**
 * Only a success or a genuine "this address does not exist" is a stable answer.
 * Every other failure (quota, key, network, or one we could not classify) must
 * stay retryable instead of poisoning the address for the rest of the TTL.
 */
export type CacheableGeocodeResult =
  | GeocodeSuccess
  | (GeocodeFailure & { status: 'ZERO_RESULTS' });

export function isCacheableGeocodeResult(
  result: GeocodeResult,
): result is CacheableGeocodeResult {
  return result.ok || result.status === 'ZERO_RESULTS';
}

async function runGeocode(input: {
  address: string;
  country: string;
  bias: LatLngPoint | null;
}): Promise<GeocodeResult> {
  const { Geocoder } = await importLibrary('geocoding');
  const { LatLngBounds } = await importLibrary('core');
  const geocoder = new Geocoder();
  const request: google.maps.GeocoderRequest = {
    address: input.address,
    componentRestrictions: { country: input.country },
  };
  if (input.bias) {
    request.bounds = new LatLngBounds(
      { lat: input.bias.lat - 0.08, lng: input.bias.lng - 0.08 },
      { lat: input.bias.lat + 0.08, lng: input.bias.lng + 0.08 },
    );
  }
  try {
    const response = await geocoder.geocode(request);
    const candidates: GeocodeCandidate[] = (response.results ?? []).map((r) => ({
      formattedAddress: r.formatted_address,
      location: { lat: r.geometry.location.lat(), lng: r.geometry.location.lng() },
      locationType: String(r.geometry.location_type ?? 'APPROXIMATE'),
      partialMatch: Boolean(r.partial_match),
      placeId: r.place_id ?? null,
    }));
    if (candidates.length === 0) {
      return {
        ok: false,
        status: 'ZERO_RESULTS',
        error: 'No matching address found.',
        hardFailure: false,
      };
    }
    return {
      ok: true,
      candidates: sortGeocodeCandidates(candidates, input.bias),
      status: 'OK',
    };
  } catch (err) {
    return mapGeocodeError(err);
  }
}

export async function geocodeAddressCached(input: {
  address: string;
  country?: string;
  bias?: LatLngPoint | null;
  boundsBias?: LatLngPoint | null;
}): Promise<GeocodeResult> {
  const trimmed = input.address.trim();
  if (!trimmed) {
    return { ok: false, status: 'ZERO_RESULTS', error: 'Empty address.', hardFailure: false };
  }
  if (!configureGoogleMapsLoader()) {
    return {
      ok: false,
      status: 'ERROR',
      error: 'Google Maps API key is not configured.',
      hardFailure: true,
    };
  }

  const country = input.country ?? 'CA';
  const bias = input.bias ?? input.boundsBias ?? null;
  const key = geocodeCacheKey({
    address: trimmed,
    country,
    biasLat: bias?.lat ?? null,
    biasLng: bias?.lng ?? null,
  });

  const hit = geocodeSessionCache.get(key) as GeocodeResult | undefined;
  if (hit) return hit;

  try {
    const result = await geocodeSessionCache.getOrLoad(key, async () => {
      const next = await runGeocode({ address: trimmed, country, bias });
      if (!isCacheableGeocodeResult(next)) {
        throw Object.assign(new Error(next.error), { geocodeResult: next });
      }
      return next;
    });
    return result as GeocodeResult;
  } catch (err) {
    const tagged = err as { geocodeResult?: GeocodeFailure };
    if (tagged.geocodeResult) return tagged.geocodeResult;
    return mapGeocodeError(err);
  }
}

export async function geocodeFirstOrNull(
  address: string,
  bias?: LatLngPoint | null,
): Promise<{ lat: number; lng: number } | null> {
  const result = await geocodeAddressCached({ address, bias });
  if (!result.ok || result.candidates.length === 0) return null;
  const loc = result.candidates[0]!.location;
  return { lat: loc.lat, lng: loc.lng };
}

export { normalizeGeocodeAddressKey };
